import { projectAuthHeaders } from '../projectAuth';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiEdit2, FiFileText, FiMessageSquare, FiMoreHorizontal, FiPlus, FiTrash2, FiStar, FiUsers } from 'react-icons/fi';
import { AUTH_API_URL, BACKEND_MAIN_URL } from '../config';
import SidebarNav from './SidebarNav';
import ChatHistory from './ChatHistory';
import { domainForAgent, inferConversationAgentId } from '../chatDomains';
import ProjectFiles from './ProjectFiles';
import Chat from './Chat';
import WorkspaceDialog from './WorkspaceDialog';
import ProjectShareModal from './ProjectShareModal';
import ProjectNotes from './ProjectNotes';
import { useOfflineSession } from '../offline/OfflineSessionContext';
import { cacheProject, getProject, setProjectInstructions } from '../offline/projectRepository';
import { syncProjectContext } from '../offline/projectSync';
import { listConversations } from '../offline/conversationRepository';

export default function ProjectPage(props) {
  const { id } = useParams();
  // Reset project-specific drafts when navigating directly between projects.
  return <ProjectWorkspace key={id} id={id} {...props} />;
}

function ProjectWorkspace({ id, onLogout, ...chatProps }) {
  const { t } = useTranslation('workspace');
  const { userId, offlineReadOnly, chatBackendUnavailable } = useOfflineSession();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [tab, setTab] = useState('discussions');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const role = project?.role || 'owner';
  const isOwner = role === 'owner';
  const canEdit = role === 'owner' || role === 'editor';
  const [selectedConversation, setSelectedConversation] = useState(undefined);
  const [activeDomain, setActiveDomain] = useState('agri');
  const [refresh, setRefresh] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [noteToDiscuss, setNoteToDiscuss] = useState(null);
  const headers = projectAuthHeaders();
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    const load = async () => {
      let cached = false;
      try {
        if (userId) {
          const local = await getProject(userId, id);
          if (active && local) { cached = true; setProject(local); setLoading(false); }
        }
        if (offlineReadOnly || chatBackendUnavailable || navigator.onLine === false) return;
        const { data } = await axios.get(`${BACKEND_MAIN_URL}/api/projects/${id}`, { headers: projectAuthHeaders() });
        const local = userId ? await cacheProject(userId, data) : data;
        if (active) setProject(local);
      } catch { if (active && !cached) setError('loadError'); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [id, retry, userId, offlineReadOnly, chatBackendUnavailable]);
  async function save(event) {
    event.preventDefault();
    if (!canEdit || saving || (editing === 'instructions' && !userId && (offlineReadOnly || chatBackendUnavailable || navigator.onLine === false)) || (editing === 'name' && (!draft.trim() || offlineReadOnly || chatBackendUnavailable || navigator.onLine === false))) return;
    setSaving(true); setError('');
    try {
      if (editing === 'instructions' && userId) {
        const local = await setProjectInstructions(userId, id, draft);
        setProject(local); setEditing(null);
        if (!offlineReadOnly && !chatBackendUnavailable && navigator.onLine !== false) {
          try { await syncProjectContext(userId, id); setProject(await getProject(userId, id)); }
          catch { /* Keep the edit queued locally for the next online retry. */ }
        }
        return;
      }
      const { data } = await axios.patch(`${BACKEND_MAIN_URL}/api/projects/${id}`, { [editing]: editing === 'name' ? draft.trim() : draft }, { headers });
      const local = userId ? await cacheProject(userId, data) : data;
      setProject(local); setEditing(null);
    } catch { setError('saveError'); } finally { setSaving(false); }
  }
  useEffect(() => {
    if (!userId) return undefined;
    const refreshContext = (event) => {
      if (String(event.detail?.projectId) !== String(id) || String(event.detail?.userId) !== String(userId)) return;
      getProject(userId, id).then((local) => { if (local) setProject(local); }).catch(() => {});
    };
    window.addEventListener('upya-project-context-synced', refreshContext);
    return () => window.removeEventListener('upya-project-context-synced', refreshContext);
  }, [id, userId]);
  async function deleteProject() {
    if (!isOwner) return;
    if (!window.confirm(t('deleteConfirm', { name: project.name }))) return;
    setSaving(true); setError('');
    try { await axios.delete(`${BACKEND_MAIN_URL}/api/projects/${id}`, { headers }); navigate('/projects'); }
    catch { setError('saveError'); } finally { setSaving(false); }
  }
  const startEdit = (field) => { if (!canEdit) return; setDraft(project[field] || ''); setError(''); setEditing(field); };
  const newChat = () => { setSelectedConversation(null); setResetKey((value) => value + 1); };
  function changeTabOnKey(event, index) {
    const keys = ['discussions', 'documents', 'context', 'notes'];
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % keys.length;
    else if (event.key === 'ArrowLeft') next = (index + keys.length - 1) % keys.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = keys.length - 1;
    else return;
    event.preventDefault(); setTab(keys[next]);
    document.getElementById(`project-tab-${keys[next]}`)?.focus();
  }
  return <div className="chat-page project-page">
    <SidebarNav onLogout={onLogout}><ChatHistory agentId="default" currentConversationId={null} onLogout={onLogout} onSelectConversation={(conversation) => navigate('/chat', { state: { conversation, timestamp: Date.now(), agentId: 'default' } })} /></SidebarNav>
    <div className="main-content ws-project-detail">
      <Link className="ws-back" to="/projects"><FiArrowLeft />{t('allProjects')}</Link>
      {/* A refetch keeps the loaded project mounted: unmounting it drops the
          documents input while the Android file picker is open. */}
      {loading && !project ? <p role="status">{t('loading')}</p> : !project ? <div role="alert"><p>{t('loadError')}</p><button className="ws-secondary" onClick={() => setRetry((value) => value + 1)}>{t('retry')}</button></div> : <>
        <div className="ws-project-title"><div><span className="ws-eyebrow">{t('projects')}</span><h1>{project.name}</h1>{project.shared && project.role !== 'owner' && <span className="ws-shared-label">{t(role === 'viewer' ? 'translation:share.shared_view_only' : 'translation:share.shared_with_me')}</span>}</div>{!offlineReadOnly && !chatBackendUnavailable && <div className="ws-project-actions"><button type="button" className="ws-secondary" onClick={() => setShareOpen(true)} aria-haspopup="dialog"><FiUsers />{t('translation:share.button')}</button><button className="ws-icon-button" type="button" aria-label={t('projectActions')} aria-haspopup="dialog" onClick={() => setEditing('actions')}><FiMoreHorizontal /></button></div>}</div>
        <div className="ws-project-tabs" role="tablist" aria-label={project.name}>{['discussions', 'documents', 'context', 'notes'].map((key, index) => <button key={key} id={`project-tab-${key}`} type="button" role="tab" aria-selected={tab === key} aria-controls={`project-panel-${key}`} tabIndex={tab === key ? 0 : -1} onKeyDown={(event) => changeTabOnKey(event, index)} onClick={() => setTab(key)}>{t(key)}{key === 'documents' && <span> {project.file_count ?? project.files?.length ?? 0}</span>}</button>)}</div>
        <section id="project-panel-discussions" role="tabpanel" aria-labelledby="project-tab-discussions" hidden={tab !== 'discussions'}>
          {selectedConversation?.mine === false && <p className="ws-shared-chat-note">{t('sharedChatCopy')}</p>}
          <Chat {...chatProps} embedded embeddedProject={project} embeddedConversation={selectedConversation} embeddedResetKey={`${id}:${resetKey}`} prefillQuestion={noteToDiscuss} onLogout={onLogout} onDomainChange={setActiveDomain} onConversationSaved={() => setRefresh((value) => value + 1)} />
          <div className="ws-section-heading"><h2>{t('discussions')}</h2><button type="button" className="ws-text-button" onClick={newChat}><FiPlus />{t('newChat')}</button></div>
          <ProjectChats projectId={id} domain={activeDomain} refresh={refresh} onSelect={setSelectedConversation} onDelete={(conversationId) => { if (selectedConversation?.id === conversationId) newChat(); }} />
        </section>
        <section id="project-panel-documents" role="tabpanel" aria-labelledby="project-tab-documents" hidden={tab !== 'documents'}>
          <div className="ws-section-heading"><h2><FiFileText />{t('documents')}</h2></div>
          <ProjectFiles key={id} canEdit={canEdit} projectId={id} files={project.files || []}
            serverCount={project.file_count ?? project.files?.length ?? 0}
            onFilesChange={(files) => {
              const next = { ...project, files, file_count: files.length };
              setProject(next);
              if (userId) cacheProject(userId, next).catch(() => {});
            }} />
        </section>
        <section id="project-panel-context" role="tabpanel" aria-labelledby="project-tab-context" hidden={tab !== 'context'}>
          <div className="ws-section-heading"><h2>{t('context')}</h2><button hidden={!canEdit} className="ws-text-button" onClick={() => startEdit('instructions')}><FiEdit2 />{t('editContext')}</button></div>
          <div className="ws-context"><p>{project.instructions || t('contextEmpty')}</p><small>{project.pendingInstructions ? t('contextPending') : t('contextApplied')}</small></div>
        </section>
        <section id="project-panel-notes" role="tabpanel" aria-labelledby="project-tab-notes" hidden={tab !== 'notes'}>
          <ProjectNotes projectId={id} canEdit={canEdit} onUseNote={(note) => { setNoteToDiscuss({ id: note.id, text: note.text }); setTab('discussions'); }} />
        </section>
        {editing === 'actions' && <WorkspaceDialog title={t('projectActions')} onClose={() => setEditing(null)}><div className="ws-tool-list"><button type="button" aria-pressed={favorite} onClick={() => setFavorite(!favorite)}><FiStar fill={favorite ? 'currentColor' : 'none'} />{t(favorite ? 'unfavorite' : 'favorite')}</button><button hidden={!canEdit} onClick={() => startEdit('name')}><FiEdit2 />{t('editName')}</button><button hidden={!isOwner} className="ws-danger" disabled={saving} onClick={deleteProject}><FiTrash2 />{t('deleteProject')}</button>{error && <p className="ws-error" role="alert">{t(error)}</p>}</div></WorkspaceDialog>}
        {(editing === 'name' || editing === 'instructions') && <WorkspaceDialog title={t(editing === 'name' ? 'editName' : 'editContext')} onClose={() => { if (!saving) setEditing(null); }}><form className="ws-form" onSubmit={save}>
          <label className="ws-field">{t(editing === 'name' ? 'name' : 'context')}{editing === 'name' ? <input autoFocus required value={draft} maxLength={100} onChange={(event) => setDraft(event.target.value)} /> : <textarea autoFocus rows={8} value={draft} maxLength={2000} placeholder={t('contextPlaceholder')} onChange={(event) => setDraft(event.target.value)} />}</label>
          {error && <p className="ws-error" role="alert">{t(error)}</p>}
          <div className="ws-form-actions"><button className="ws-secondary" type="button" disabled={saving} onClick={() => setEditing(null)}>{t('cancel')}</button><button className="ws-primary" disabled={saving}>{t(saving ? 'saving' : 'save')}</button></div>
        </form></WorkspaceDialog>}
        {shareOpen && <ProjectShareModal project={project} onClose={() => setShareOpen(false)} />}
      </>}
    </div>
  </div>;
}

function ProjectChats({ projectId, domain, refresh, onSelect, onDelete }) {
  const { t, i18n } = useTranslation('workspace');
  const { userId, offlineReadOnly, chatBackendUnavailable } = useOfflineSession();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [deleting, setDeleting] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    if (offlineReadOnly || chatBackendUnavailable || navigator.onLine === false) {
      listConversations(userId, { projectId }).then((rows) => { if (active) setChats(rows); })
        .catch(() => { if (active) setError('loadError'); }).finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }
    axios.get(`${AUTH_API_URL}/conversations?projectId=${encodeURIComponent(projectId)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(({ data }) => {
        let map = {};
        try { map = JSON.parse(localStorage.getItem('projectChatLinks') || '{}'); } catch {}
        if (active) setChats((Array.isArray(data) ? data : []).filter((chat) => chat.projectId === projectId || map[chat.id] === projectId));
      }).catch(async () => {
        try { const rows = await listConversations(userId, { projectId }); if (active) setChats(rows); }
        catch { if (active) setError('loadError'); }
      }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId, refresh, retry, userId, offlineReadOnly, chatBackendUnavailable]);
  async function remove(chat) {
    if (chat.mine === false) return;
    if (!window.confirm(t('deleteChatConfirm'))) return;
    setDeleting(chat.id); setError('');
    try {
      await axios.delete(`${AUTH_API_URL}/conversations/${encodeURIComponent(chat.id)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setChats((previous) => previous.filter((item) => item.id !== chat.id));
      try { const map = JSON.parse(localStorage.getItem('projectChatLinks') || '{}'); delete map[chat.id]; localStorage.setItem('projectChatLinks', JSON.stringify(map)); } catch {}
      onDelete(chat.id);
    } catch { setError('saveError'); } finally { setDeleting(null); }
  }
  const visibleChats = chats.filter((chat) => domainForAgent(inferConversationAgentId(chat)) === domain);
  return <div className="ws-project-conversations">
    {error && <div role="alert" className="ws-error"><p>{t(error)}</p><button className="ws-secondary" onClick={() => setRetry((value) => value + 1)}>{t('retry')}</button></div>}
    {loading ? <p role="status">{t('loading')}</p> : visibleChats.length === 0 ? <p className="ws-muted">{t('noHistory')}</p> : visibleChats.map((chat) => <div className="ws-conversation-row" key={chat.id}><button className="ws-history-link" onClick={() => onSelect(chat)}><FiMessageSquare /><span>{chat.title || t('untitled')}{chat.mine === false && chat.author && <small>{chat.author.name || chat.author.email}</small>}{chat.updatedAt && <small>{new Date(chat.updatedAt).toLocaleDateString(i18n.resolvedLanguage)}</small>}</span></button>{chat.mine !== false && <button className="ws-icon-button" disabled={deleting === chat.id} onClick={() => remove(chat)} aria-label={t('deleteChat')}><FiTrash2 /></button>}</div>)}
  </div>;
}
